/**
 * Trade log parsing and analysis.
 *
 * The native format is what the backtest engine writes to `data/results`:
 * `Symbol, Side, Qty, EntryTime, ExitTime, EntryPrice, ExitPrice, P/L, Fees`
 * where a negative position is a short, `Side` carries the direction, and `P/L` is
 * already net of `Fees`. TradingView exports (parenthesised headers, suffixed duplicates,
 * a `P/L (%)` column) are still accepted through column aliases.
 *
 * Algotest backtest exports are recognised by their headers and converted into the native
 * CSV by `convertAlgotestToTradeLogCsv` before parsing, so every metric below runs on one
 * format only. See the Algotest adaptor section for the dialect's quirks.
 */

/** The backtest engine's column order. Kept in sync by tests/backtest/trade-log-format.test.ts. */
export const TRADE_LOG_COLUMNS = [
    "Symbol",
    "Side",
    "Qty",
    "EntryTime",
    "ExitTime",
    "EntryPrice",
    "ExitPrice",
    "P/L",
    "Fees",
] as const;

/** Logs are stamped in exchange time, so every bucket and label uses it, not the browser's zone. */
export const TRADE_LOG_TIMEZONE = "Asia/Kolkata";
/** IST is a fixed +05:30 with no daylight saving, which keeps parsing offset-free. */
const IST_OFFSET_MINUTES = 330;
/** How converted timestamps are stamped, matching what the backtest engine writes. */
const IST_ZONE_SUFFIX = "+05:30";

export type TradeSide = "long" | "short";

export type Trade = {
    symbol: string;
    side: "LONG" | "SHORT";
    quantity: number;
    entryPrice: number;
    exitPrice: number;
    entryDate: Date;
    exitDate: Date;
    /** P/L as reported by the log, gross of fees unless the file says otherwise. */
    pnl: number;
    /** Taxes and charges on this trade: the log's own fees, or the model's when costs are applied. */
    fees: number;
    /** Fixed brokerage charged on this trade's orders by the cost model. */
    brokerage: number;
    /** Cost of the slippage model on this trade's orders. */
    slippage: number;
    /** What the strategy actually earned: `pnl` less fees, brokerage and slippage. */
    netPnl: number;
    pnlPct: number;
    /** Still open at the end of the log, so it has no realized P/L yet. */
    isOpen: boolean;
};

export type MonthlyPnL = {
    year: number;
    month: number;
    monthLabel: string;
    pnl: number;
    trades: number;
    winRate: number;
};

/**
 * Expectancy with each trading day as one outcome. A day is a win, a loss or flat by its net P&L,
 * so this shows whether a strategy's typical day pays for its typical bad day.
 */
export type DailyExpectancy = {
    /** Trading days that closed at least one trade. */
    days: number;
    winningDays: number;
    losingDays: number;
    /** Share of trading days that finished positive, as a percentage. */
    dayWinRate: number;
    avgWinningDay: number;
    /** Average loss on a losing day, as a positive number. */
    avgLosingDay: number;
    /** Expected net P&L per trading day: win-day rate × avg winning day − loss-day rate × avg losing day. */
    expectancy: number;
    /** Expectancy in units of the average losing day. */
    expectancyRatio: number;
};

/** One year of the monthly grid: P&L per month (null where the month had no closed trade), and that year's own max drawdown. */
export type YearMonthRow = {
    year: number;
    months: (number | null)[];
    /** The year's net P&L: the sum of its months. */
    pnl: number;
    maxDrawdown: number;
};

export type TimeWindowPnL = {
    /** `09:15–09:30`, in exchange time. */
    window: string;
    pnl: number;
    trades: number;
    winRate: number;
};

export type WeekdayPnL = {
    /** `Sunday` … `Saturday`. */
    day: string;
    pnl: number;
    trades: number;
    winRate: number;
};

export type YearlyPnL = {
    year: number;
    pnl: number;
    trades: number;
    winRate: number;
    avgPnl: number;
};

export type EquityPoint = {
    date: string;
    equity: number;
    drawdown: number;
};

export type SymbolStats = {
    symbol: string;
    trades: number;
    pnl: number;
    winRate: number;
    avgPnl: number;
    bestTrade: number;
    worstTrade: number;
};

export type HistogramBin = {
    range: string;
    from: number;
    count: number;
    fill: string;
};

export type Analysis = {
    trades: Trade[];
    closedTrades: number;
    openTrades: number;
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    breakevenTrades: number;
    totalPnl: number;
    grossProfit: number;
    grossLoss: number;
    netPnl: number;
    totalFees: number;
    totalBrokerage: number;
    totalSlippage: number;
    /** Fees + brokerage + slippage on closed trades. */
    totalCosts: number;
    winRate: number;
    lossRate: number;
    profitFactor: number;
    expectancy: number;
    payoffRatio: number;
    avgWin: number;
    avgLoss: number;
    largestWin: number;
    largestLoss: number;
    medianWin: number;
    medianLoss: number;
    maxDrawdown: number;
    maxDrawdownPct: number;
    /** Milliseconds spent under water at the worst point, 0 if never recovered. */
    maxDrawdownDurationMs: number;
    recoveryFactor: number;
    sharpeRatio: number;
    sortinoRatio: number;
    avgTradeDuration: number;
    medianTradeDuration: number;
    longestTrade: number;
    shortestTrade: number;
    maxConsecutiveWins: number;
    maxConsecutiveLosses: number;
    avgConsecutiveWins: number;
    avgConsecutiveLosses: number;
    bestDay: { date: string; pnl: number };
    worstDay: { date: string; pnl: number };
    bestMonth: MonthlyPnL;
    worstMonth: MonthlyPnL;
    bestYear: { year: number; pnl: number };
    worstYear: { year: number; pnl: number };
    avgDailyPnl: number;
    avgMonthlyPnl: number;
    /** Expectancy in units of the average loss: win rate × payoff ratio − loss rate. */
    expectancyRatio: number;
    /** Calendar years from the first closed entry to the last closed exit. */
    tradingYears: number;
    /** Recovery factor (net P&L ÷ max drawdown) divided by `tradingYears`. */
    avgReturnToMaxDrawdown: number;
    /** Monthly grid by year, each with that year's own max drawdown. */
    monthGrid: YearMonthRow[];
    dailyExpectancy: DailyExpectancy;
    longTrades: { count: number; pnl: number; winRate: number; avgPnl: number };
    shortTrades: { count: number; pnl: number; winRate: number; avgPnl: number };
    equityCurve: EquityPoint[];
    monthlyPnl: MonthlyPnL[];
    /** P&L by the 15 minute window the trade was entered in. */
    entryWindowPnl: TimeWindowPnL[];
    /** P&L by the 15 minute window the trade was exited in. */
    exitWindowPnl: TimeWindowPnL[];
    /** P&L by weekday, Sunday first. */
    weekdayPnl: WeekdayPnL[];
    yearlyPnl: YearlyPnL[];
    symbolStats: SymbolStats[];
    pnlDistribution: HistogramBin[];
    dailyPnl: { date: string; pnl: number }[];
};

/** Which dialect an uploaded CSV was recognised as before it reached the trade parser. */
export type TradeLogFormat = "backtest" | "algotest" | "generic";

export type ParsedTradeLog = {
    trades: Trade[];
    headers: string[];
    /** True when the P/L column already has fees deducted, as the backtest engine writes it. */
    pnlIsNetOfFees: boolean;
    /** Rows dropped because they carried no usable timestamp. */
    skippedRows: number;
    /** The dialect the upload was recognised as; Algotest files are converted before parsing. */
    format: TradeLogFormat;
    /** Set when the file could not be read at all. */
    error?: string;
};

export type ParseOptions = {
    /** Overrides auto-detection, for logs whose P/L column means the other thing. */
    pnlIsNetOfFees?: boolean;
};

// ─── Position scaling ───────────────────────────────────────────────────────

/**
 * Re-prices a trade at a different position size, so a log can be read as if the same
 * entries and exits had been taken with more or fewer units. Quantity, P&L, fees and
 * modelled costs all move with the size; entry/exit prices and the return percentage do not.
 */
export function scaleTrade(trade: Trade, multiplier: number): Trade {
    if (!Number.isFinite(multiplier) || multiplier === 1) return trade;
    return {
        ...trade,
        quantity: trade.quantity * multiplier,
        pnl: trade.pnl * multiplier,
        fees: trade.fees * multiplier,
        brokerage: trade.brokerage * multiplier,
        slippage: trade.slippage * multiplier,
        netPnl: trade.netPnl * multiplier,
    };
}

export function scaleTrades(trades: Trade[], multiplier: number): Trade[] {
    if (!Number.isFinite(multiplier) || multiplier === 1) return trades;
    return trades.map((trade) => scaleTrade(trade, multiplier));
}

// ─── Cost model ─────────────────────────────────────────────────────────────

/** The bucket a symbol trades in, which decides its charge rates. */
export type InstrumentKind = "equity" | "futures" | "options";

/** What one bucket costs on each side of a trade, as a percentage of traded value. */
export type ChargeRate = {
    /** Charged on a BUY. */
    buy: number;
    /** Charged on a SELL. */
    sell: number;
};

/** One rate per side, per instrument kind. */
export type ChargeRates = Record<InstrumentKind, ChargeRate>;

/**
 * The client-side taxes-and-charges model, carried over from the backtest broker's defaults.
 * Rates are percentages of traded value (`0.219` = 0.219%), not fractions.
 */
export const DEFAULT_CHARGE_RATES: ChargeRates = {
    equity: { buy: 0.0050, sell: 0.0612 },
    futures: { buy: 0.0050, sell: 0.0612 },
    options: { buy: 0.0550, sell: 0.2190 },
};

/**
 * Which bucket a logged symbol trades in.
 *
 * A cash exchange answers outright: anything on NSE/BSE is equity, however its ticker is spelled.
 * Otherwise the suffix carries the family — `FUT`, `CE`, `PE` — which also recognises the
 * converted Algotest symbols (`10650 CE`, `10850 PE`). Anything unrecognised is treated as equity,
 * matching the broker model this replaces.
 */
export function instrumentKindOf(symbol: string): InstrumentKind {
    const separator = symbol.indexOf(":");
    const exchange = (separator === -1 ? "" : symbol.slice(0, separator)).trim().toUpperCase();
    if (exchange === "NSE" || exchange === "BSE") return "equity";
    const tradingSymbol = (separator === -1 ? symbol : symbol.slice(separator + 1)).trim().toUpperCase();
    if (tradingSymbol.endsWith("FUT")) return "futures";
    if (tradingSymbol.endsWith("CE") || tradingSymbol.endsWith("PE")) return "options";
    return "equity";
}

/** What the page applies on top of the logged P/L. Every switch defaults to off / zero. */
export type TradeCosts = {
    /** Apply `DEFAULT_CHARGE_RATES` — taxes and exchange charges — on every order. */
    charges: boolean;
    /** Percentage of traded value lost to slippage on every order; 0 disables. */
    slippagePct: number;
    /** Fixed brokerage charged on every order, in currency; 0 disables. */
    brokeragePerOrder: number;
};

export const NO_TRADE_COSTS: TradeCosts = { charges: false, slippagePct: 0, brokeragePerOrder: 0 };

/** One fill of a position: entry always, exit only once the position has closed. */
type TradeOrder = { side: "buy" | "sell"; price: number; quantity: number };

function tradeOrders(trade: Trade): TradeOrder[] {
    const entrySide = trade.side === "LONG" ? "buy" : "sell";
    const exitSide = trade.side === "LONG" ? "sell" : "buy";
    const orders: TradeOrder[] = [{ side: entrySide, price: trade.entryPrice, quantity: trade.quantity }];
    if (!trade.isOpen) orders.push({ side: exitSide, price: trade.exitPrice, quantity: trade.quantity });
    return orders;
}

export type TradeCostBreakdown = {
    /** Taxes and charges: the model's when enabled, otherwise the log's own fees. */
    fees: number;
    brokerage: number;
    slippage: number;
    /** Entry plus exit fills: 2 for a closed position, 1 for an open one. */
    orders: number;
};

/** What the model charges for one trade at its logged size. */
export function tradeCostsFor(
    trade: Trade,
    costs: TradeCosts,
    rates: ChargeRates = DEFAULT_CHARGE_RATES,
): TradeCostBreakdown {
    const orders = tradeOrders(trade);
    const kind = instrumentKindOf(trade.symbol);
    const slippagePct = Math.max(0, Number.isFinite(costs.slippagePct) ? costs.slippagePct : 0);
    const brokeragePerOrder = Math.max(0, Number.isFinite(costs.brokeragePerOrder) ? costs.brokeragePerOrder : 0);

    let charges = 0;
    let slippage = 0;
    for (const order of orders) {
        const tradedValue = Math.abs(order.price * order.quantity);
        if (costs.charges) charges += (tradedValue * rates[kind][order.side]) / 100;
        slippage += (tradedValue * slippagePct) / 100;
    }

    return {
        fees: costs.charges ? charges : trade.fees,
        brokerage: brokeragePerOrder * orders.length,
        slippage,
        orders: orders.length,
    };
}

/**
 * Applies the cost model to parsed trades, returning new trade objects.
 *
 * Charges replace the log's own fee column when enabled, so re-analysing a log under this model
 * does not pay the same toll twice. Brokerage is charged per order — two for a closed position,
 * one for a position still open. Slippage is a percentage of each fill's traded value.
 *
 * `pnlIsNetOfFees` says whether the log's P/L column already had its own fees deducted; when it
 * did, those fees are added back before the model's costs are taken out.
 */
export function applyTradeCosts(
    trades: Trade[],
    costs: TradeCosts,
    pnlIsNetOfFees = false,
    rates: ChargeRates = DEFAULT_CHARGE_RATES,
): Trade[] {
    if (!costs.charges && costs.slippagePct <= 0 && costs.brokeragePerOrder <= 0) return trades;

    return trades.map((trade) => {
        const breakdown = tradeCostsFor(trade, costs, rates);
        const grossPnl = pnlIsNetOfFees ? trade.pnl + trade.fees : trade.pnl;
        const netPnl = grossPnl - breakdown.fees - breakdown.brokerage - breakdown.slippage;
        const entryNotional = Math.abs(trade.entryPrice * trade.quantity);
        const pnlPct = trade.isOpen || entryNotional === 0 ? 0 : (netPnl / entryNotional) * 100;

        return {
            ...trade,
            fees: breakdown.fees,
            brokerage: breakdown.brokerage,
            slippage: breakdown.slippage,
            netPnl,
            pnlPct,
        };
    });
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WINDOW_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;

/** `HH:mm` for a minute of the day. */
const clockLabel = (minutes: number): string =>
    `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** The minute of the day an instant falls on, in exchange time. */
const istMinuteOfDay = (date: Date): number =>
    (((Math.floor(date.getTime() / 60_000) + IST_OFFSET_MINUTES) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

/**
 * P&L per 15 minute window of the day, keyed by the moment `dateOf` picks out of each trade.
 * Every window between the first and last one that saw a trade is listed, so quiet windows read as zero.
 */
function windowBreakdown(trades: Trade[], dateOf: (trade: Trade) => Date): TimeWindowPnL[] {
    const totals = new Map<number, { pnl: number; trades: number; wins: number }>();
    for (const t of trades) {
        const date = dateOf(t);
        if (!isValidDate(date)) continue;
        const start = Math.floor(istMinuteOfDay(date) / WINDOW_MINUTES) * WINDOW_MINUTES;
        const existing = totals.get(start) ?? { pnl: 0, trades: 0, wins: 0 };
        existing.pnl += t.netPnl;
        existing.trades++;
        if (t.netPnl > 0) existing.wins++;
        totals.set(start, existing);
    }
    if (totals.size === 0) return [];
    const starts = [...totals.keys()];
    const first = Math.min(...starts);
    const last = Math.max(...starts);
    const rows: TimeWindowPnL[] = [];
    for (let start = first; start <= last; start += WINDOW_MINUTES) {
        const v = totals.get(start) ?? { pnl: 0, trades: 0, wins: 0 };
        rows.push({
            window: `${clockLabel(start)}–${clockLabel(start + WINDOW_MINUTES)}`,
            pnl: v.pnl,
            trades: v.trades,
            winRate: v.trades > 0 ? (v.wins / v.trades) * 100 : 0,
        });
    }
    return rows;
}
export const POSITIVE_COLOR = "#10b981";
export const NEGATIVE_COLOR = "#ef4444";
export const ACCENT_COLOR = "#6366f1";
export const NEUTRAL_COLOR = "#94a3b8";

// ─── CSV parsing ────────────────────────────────────────────────────────────

/**
 * Aliases per field, most specific first. Keys are compared against headers normalised
 * by `normalizeHeader`, so `Date/Time (entry)`, `EntryTime` and `entry_time` all match.
 */
const COLUMN_ALIASES = {
    symbol: ["symbol", "ticker", "instrument", "contract", "tradingsymbol", "underlying", "name"],
    side: ["side", "sidetype", "type", "direction", "tradetype", "position"],
    quantity: ["qty", "quantity", "qtyquantity", "size", "units", "position size"],
    entryTime: ["entrytime", "datetimeentry", "entrydatetime", "entrydate", "datetimeopen", "opendatetime", "opendate", "dateopened", "datetime"],
    exitTime: ["exittime", "datetimeexit", "exitdatetime", "exitdate", "datetimeclose", "closedatetime", "closedate", "dateclosed", "datetime1", "datetime2", "date"],
    entryPrice: ["entryprice", "priceentry", "openprice", "avgentryprice", "entry", "price"],
    exitPrice: ["exitprice", "priceexit", "closeprice", "avgexitprice", "exit", "price1", "price2"],
    pnl: ["pl", "pnl", "netpl", "netpnl", "netplpercent", "pnlamount", "realizedpl", "profit", "profitloss", "plamount", "pl1", "pnl1"],
    fees: ["fees", "fee", "commission", "commissions", "cost", "costs", "charges"],
    pnlPct: ["plpercent", "plpct", "pnlpercent", "returnpercent", "pnlpct", "pl2", "pnl2"],
} as const;

type ColumnName = keyof typeof COLUMN_ALIASES;

/** `P/L (%)` → `plpercent`, `Price.1` → `price1`, so aliases can be written without punctuation. */
function normalizeHeader(value: string): string {
    return value
        .toLowerCase()
        .replaceAll("%", "percent")
        .replaceAll(/[^a-z0-9]/g, "");
}

/**
 * Resolves a field to a column index, preferring an exact alias hit and never handing back
 * a column another field already claimed (TradingView repeats headers as `Price` / `Price.1`).
 */
function resolveColumn(headers: string[], field: ColumnName, used: Set<number>): number {
    const normalized = headers.map(normalizeHeader);
    const aliases = COLUMN_ALIASES[field] as readonly string[];
    for (const alias of aliases) {
        const idx = normalized.findIndex((h, i) => h === normalizeHeader(alias) && !used.has(i));
        if (idx !== -1) return idx;
    }
    for (const alias of aliases) {
        const target = normalizeHeader(alias);
        const idx = normalized.findIndex((h, i) => !used.has(i) && h.length > 2 && (h.includes(target) || target.includes(h)));
        if (idx !== -1) return idx;
    }
    return -1;
}

function detectDelimiter(headerLine: string): string {
    if (headerLine.includes("\t")) return "\t";
    if (headerLine.includes(";")) return ";";
    return ",";
}

/** Splits CSV text into rows and cells, honouring quoted cells that contain the delimiter. */
function splitCsvRows(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let quoted = false;

    for (let i = 0; i < text.length; i++) {
        const char = text[i]!;
        if (quoted) {
            if (char === '"') {
                if (text[i + 1] === '"') {
                    cell += '"';
                    i++;
                } else {
                    quoted = false;
                }
            } else {
                cell += char;
            }
            continue;
        }
        if (char === '"' && cell.trim() === "") {
            quoted = true;
            cell = "";
            continue;
        }
        if (char === delimiter) {
            row.push(cell.trim());
            cell = "";
            continue;
        }
        if (char === "\r") continue;
        if (char === "\n") {
            row.push(cell.trim());
            rows.push(row);
            row = [];
            cell = "";
            continue;
        }
        cell += char;
    }
    if (cell !== "" || row.length > 0) {
        row.push(cell.trim());
        rows.push(row);
    }
    return rows.filter((cells) => cells.some((cell) => cell !== ""));
}

function parseNumber(value: string | undefined): number {
    if (!value) return 0;
    let v = value.trim();
    if (v === "") return 0;
    // Accounting negatives: (1,234.50)
    const accounting = /^\((.*)\)$/.exec(v);
    if (accounting) {
        v = `-${accounting[1]}`;
    }
    v = v.replace(/[₹$€£¥%\s,]/g, "");
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Reads a log timestamp. Values carrying an offset are handed to `Date` as is; values
 * without one are exchange wall clock time and are read as IST.
 */
export function parseIstDate(value: string | undefined): Date {
    const raw = (value ?? "").trim();
    if (raw === "") return new Date(NaN);
    const match =
        /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(Z|[+-]\d{2}:?\d{2})?)?/.exec(raw);
    if (!match) {
        const fallback = new Date(raw);
        return Number.isNaN(fallback.getTime()) ? new Date(NaN) : fallback;
    }
    const [, year, month, day, hour, minute, second, zone] = match;
    if (zone) return new Date(raw.replace(" ", "T"));
    return new Date(
        Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour ?? 0) - Math.floor(IST_OFFSET_MINUTES / 60),
            Number(minute ?? 0) - (IST_OFFSET_MINUTES % 60),
            Number(second ?? 0),
        ),
    );
}

function isValidDate(date: Date): boolean {
    return date instanceof Date && !Number.isNaN(date.getTime());
}

const istDayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: TRADE_LOG_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

/** `yyyy-mm-dd` in exchange time. Bucketing in UTC would push IST morning trades onto the previous day. */
export function tradeDateKey(date: Date, fallback = "Unknown"): string {
    return isValidDate(date) ? istDayFormatter.format(date) : fallback;
}

function toSide(quantity: number): "LONG" | "SHORT" {
    return quantity < 0 ? "SHORT" : "LONG";
}

function parseSide(value: string | undefined, quantity: number): "LONG" | "SHORT" {
    const raw = (value ?? "").trim().toLowerCase();
    if (raw === "") return toSide(quantity);
    if (raw.startsWith("s") || raw.includes("sell") || raw.includes("short")) return "SHORT";
    if (raw.startsWith("b") || raw.includes("buy") || raw.includes("long")) return "LONG";
    return toSide(quantity);
}

/**
 * Every column of the backtest format is present, so the log is ours: direction comes from
 * `Side`, `Qty` is unsigned, and `P/L` has already had `Fees` taken out.
 */
function isBacktestFormat(headers: string[]): boolean {
    const normalized = new Set(headers.map(normalizeHeader));
    return TRADE_LOG_COLUMNS.every((column) => normalized.has(normalizeHeader(column)));
}

// ─── Algotest adaptor ───────────────────────────────────────────────────────

/**
 * Algotest ships two dialects, identified by the columns nothing else carries. Both are
 * recognised before the generic column matcher gets a chance to mis-read `Type` or
 * `Instrument-Kind` as the side and `Entry Time` as a date.
 */
const ALGOTEST_SIGNATURE_COLUMNS = {
    /** The strategy export: deltas, expiry and remarks per row, hyphenated column names. */
    trade: ["Entry-Date", "Entry-Time", "StrikePrice", "Instrument-Kind", "P/L"],
    /** The portfolio export: fully quoted cells, `Type`/`Strike`/`B/S` columns and a Vix column. */
    portfolio: ["Entry-Date", "Entry-Time", "Exit-Date", "Exit-Time", "Strike", "B/S", "Qty", "P/L"],
} as const;

/**
 * Column names per field across both dialects, tried in order against normalised headers.
 * Hyphen and space variants (`Entry-Date` / `Entry Date`) normalise to the same key, so the
 * first spelling that exists in the file wins.
 */
const ALGOTEST_COLUMN_ALIASES = {
    entryDate: ["Entry-Date", "Entry Date"],
    entryTime: ["Entry-Time", "Entry Time"],
    entryPrice: ["Entry-Price", "Entry Price"],
    quantity: ["Quantity", "Qty"],
    instrumentKind: ["Instrument-Kind", "Type"],
    strikePrice: ["StrikePrice", "Strike"],
    position: ["Position", "B/S"],
    exitDate: ["ExitDate", "Exit Date"],
    exitTime: ["ExitTime", "Exit Time"],
    exitPrice: ["ExitPrice", "Exit Price"],
    pnl: ["P/L"],
} as const;

export function isAlgotestCsv(csvText: string): boolean {
    const text = csvText.replace(/^\uFEFF/, "");
    const firstLine = text.split(/\r?\n/).find((line) => line.trim() !== "") ?? "";
    if (firstLine === "") return false;
    const headers = splitCsvRows(firstLine, detectDelimiter(firstLine))[0] ?? [];
    const normalized = new Set(headers.map(normalizeHeader));
    return Object.values(ALGOTEST_SIGNATURE_COLUMNS).some((signature) =>
        signature.every((column) => normalized.has(normalizeHeader(column))),
    );
}

/**
 * Algotest writes `Entry-Date` and `Entry-Time` as separate IST wall-clock cells; the native
 * format wants one timestamp. A missing time reads as midnight, a missing date as blank so
 * the parser can drop the row rather than invent one.
 */
function joinAlgotestTimestamp(date: string, time: string): string {
    if (!date) return "";
    if (!time) return date;
    return `${date}T${time}${IST_ZONE_SUFFIX}`;
}

function escapeCsvCell(value: string): string {
    return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * Converts an Algotest export into the native CSV, one row per option leg.
 *
 * Algotest groups rows by strategy trade: a parent row (integer `Index`) summarises the
 * underlying position, and each leg row (`1.1`, `1.2`, …) carries one option fill. A parent's
 * P/L is exactly the sum of its legs' P/L, so converting parents as well would double count;
 * legs are kept and parents skipped. A row that carries a quantity of its own — a futures or
 * underlying trade with no legs — is kept as is. Algotest reports no fees, so `Fees` is 0
 * and the P/L column lands net of fees, which is what the native format expects.
 *
 * Both dialects convert through the same path: the column names differ, the row shape does not.
 */
export function convertAlgotestToTradeLogCsv(csvText: string): string {
    const text = csvText.replace(/^\uFEFF/, "");
    const firstLine = text.split(/\r?\n/).find((line) => line.trim() !== "") ?? "";
    const rows = splitCsvRows(text, detectDelimiter(firstLine));
    if (rows.length < 2) return "";

    const normalized = (rows[0] ?? []).map((header) => normalizeHeader(header.trim()));
    const col = (field: keyof typeof ALGOTEST_COLUMN_ALIASES) => {
        for (const alias of ALGOTEST_COLUMN_ALIASES[field]) {
            const idx = normalized.indexOf(normalizeHeader(alias));
            if (idx !== -1) return idx;
        }
        return -1;
    };

    const idxEntryDate = col("entryDate");
    const idxEntryTime = col("entryTime");
    const idxEntryPrice = col("entryPrice");
    const idxQuantity = col("quantity");
    const idxInstrumentKind = col("instrumentKind");
    const idxStrikePrice = col("strikePrice");
    const idxPosition = col("position");
    const idxExitDate = col("exitDate");
    const idxExitTime = col("exitTime");
    const idxExitPrice = col("exitPrice");
    const idxPnl = col("pnl");

    const out: string[] = [TRADE_LOG_COLUMNS.join(",")];
    for (let i = 1; i < rows.length; i++) {
        const values = rows[i]!;
        const cell = (idx: number) => (idx === -1 ? "" : (values[idx] ?? "").trim());

        // Rows without a quantity are parent summaries of the rows below them, not trades.
        const quantity = parseNumber(cell(idxQuantity));
        if (cell(idxQuantity) === "" || quantity === 0) continue;

        // `10650 CE` from strike + kind; a leg without a kind is the underlying itself.
        const strike = parseNumber(cell(idxStrikePrice));
        const kind = cell(idxInstrumentKind).toUpperCase();
        const symbol =
            kind !== ""
                ? `${strike > 0 ? String(strike) : "UNDERLYING"} ${kind}`
                : strike > 0
                    ? String(strike)
                    : "UNDERLYING";

        const side = parseSide(cell(idxPosition), quantity) === "SHORT" ? "short" : "long";
        out.push(
            [
                escapeCsvCell(symbol),
                side,
                String(quantity),
                joinAlgotestTimestamp(cell(idxEntryDate), cell(idxEntryTime)),
                joinAlgotestTimestamp(cell(idxExitDate), cell(idxExitTime)),
                cell(idxEntryPrice),
                cell(idxExitPrice),
                cell(idxPnl),
                "0",
            ].join(","),
        );
    }
    return out.join("\n") + "\n";
}

export function parseTradeLogCsv(csvText: string, options: ParseOptions = {}): ParsedTradeLog {
    const text = csvText.replace(/^\uFEFF/, "");
    // Algotest is recognised first and rewritten into the native CSV, so everything below —
    // column matching, P/L semantics, open-trade rules — sees one format only.
    if (isAlgotestCsv(text)) {
        const converted = parseTradeLogCsv(convertAlgotestToTradeLogCsv(text), options);
        return { ...converted, format: "algotest" };
    }
    const firstLine = text.split(/\r?\n/).find((line) => line.trim() !== "") ?? "";
    const delimiter = detectDelimiter(firstLine);
    const rows = splitCsvRows(text, delimiter);
    if (rows.length < 2)
        return { trades: [], headers: [], pnlIsNetOfFees: false, skippedRows: 0, format: "generic" };

    const headers = rows[0]!.map((header) => header.replace(/^﻿/, "").trim());
    const used = new Set<number>();
    const pick = (field: ColumnName) => {
        const idx = resolveColumn(headers, field, used);
        if (idx !== -1) used.add(idx);
        return idx;
    };

    const symbolIdx = pick("symbol");
    const sideIdx = pick("side");
    const quantityIdx = pick("quantity");
    const entryTimeIdx = pick("entryTime");
    const exitTimeIdx = pick("exitTime");
    const entryPriceIdx = pick("entryPrice");
    const exitPriceIdx = pick("exitPrice");
    const pnlIdx = pick("pnl");
    const feesIdx = pick("fees");
    const pnlPctIdx = pick("pnlPct");

    const pnlIsNetOfFees = options.pnlIsNetOfFees ?? isBacktestFormat(headers);
    const cell = (values: string[], idx: number) => (idx === -1 ? "" : (values[idx] ?? "").trim());

    const trades: Trade[] = [];
    let skippedRows = 0;

    for (let i = 1; i < rows.length; i++) {
        const values = rows[i]!;
        const symbol = cell(values, symbolIdx) || "UNKNOWN";
        // Signed quantities encode the side, and the backtest format reports the size unsigned.
        const signedQuantity = quantityIdx === -1 ? 1 : parseNumber(cell(values, quantityIdx));
        const quantity = Math.abs(signedQuantity);
        const side = parseSide(cell(values, sideIdx), signedQuantity);
        if (quantity === 0) continue;

        const entryDate = parseIstDate(cell(values, entryTimeIdx));
        const exitDate = parseIstDate(cell(values, exitTimeIdx));
        const entryPrice = parseNumber(cell(values, entryPriceIdx));
        const exitPrice = parseNumber(cell(values, exitPriceIdx));
        const fees = Math.abs(parseNumber(cell(values, feesIdx)));

        // An open position has no exit fill yet, so it carries no realized P/L.
        const isOpen = !isValidDate(exitDate) || exitPrice === 0;
        if (!isValidDate(entryDate) && !isValidDate(exitDate)) {
            skippedRows++;
            continue;
        }

        const direction = side === "LONG" ? 1 : -1;
        const pricePnl = isOpen ? 0 : (exitPrice - entryPrice) * quantity * direction;
        const pnl = pnlIdx === -1 ? pricePnl : parseNumber(cell(values, pnlIdx));
        const netPnl = pnlIsNetOfFees ? pnl : pnl - fees;
        const entryNotional = Math.abs(entryPrice * quantity);
        const pnlPct =
            !isOpen && pnlPctIdx !== -1
                ? parseNumber(cell(values, pnlPctIdx))
                : isOpen || entryNotional === 0
                    ? 0
                    : (netPnl / entryNotional) * 100;

        trades.push({
            symbol,
            side,
            quantity,
            entryPrice,
            exitPrice: isOpen ? 0 : exitPrice,
            entryDate,
            exitDate: isOpen ? new Date(NaN) : exitDate,
            pnl,
            fees,
            brokerage: 0,
            slippage: 0,
            netPnl,
            pnlPct,
            isOpen,
        });
    }

    trades.sort((a, b) => tradeTimestamp(a) - tradeTimestamp(b));
    return { trades, headers, pnlIsNetOfFees, skippedRows, format: isBacktestFormat(headers) ? "backtest" : "generic" };
}

function tradeTimestamp(trade: Trade): number {
    if (isValidDate(trade.entryDate)) return trade.entryDate.getTime();
    if (isValidDate(trade.exitDate)) return trade.exitDate.getTime();
    return 0;
}

// ─── Analysis ───────────────────────────────────────────────────────────────

function mean(values: number[]): number {
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
}

/** Pearson correlation of two equal-length series; NaN when either one never moves. */
function pearson(x: number[], y: number[]): number {
    const n = x.length;
    if (n < 2) return NaN;
    const mx = mean(x);
    const my = mean(y);
    let cov = 0;
    let vx = 0;
    let vy = 0;
    for (let i = 0; i < n; i++) {
        const dx = x[i]! - mx;
        const dy = y[i]! - my;
        cov += dx * dy;
        vx += dx * dx;
        vy += dy * dy;
    }
    if (vx === 0 || vy === 0) return NaN;
    return cov / Math.sqrt(vx * vy);
}

/**
 * Correlation between strategies' daily net P&L, one row and column per series. The series are
 * aligned on every date any of them traded, and a strategy that did not trade a day counts as 0 that day.
 */
export function correlationMatrix(series: { date: string; pnl: number }[][]): number[][] {
    const dates = [...new Set(series.flatMap((daily) => daily.map((d) => d.date)))].sort();
    const vectors = series.map((daily) => {
        const byDate = new Map<string, number>(daily.map((d) => [d.date, d.pnl]));
        return dates.map((date) => byDate.get(date) ?? 0);
    });
    return vectors.map((x) => vectors.map((y) => pearson(x, y)));
}

function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid]! : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function computeConsecutiveStreaks(trades: Trade[]): {
    maxWins: number;
    maxLosses: number;
    avgWins: number;
    avgLosses: number;
} {
    const winStreaks: number[] = [];
    const lossStreaks: number[] = [];
    let currentWin = 0;
    let currentLoss = 0;

    for (const t of trades) {
        if (t.netPnl > 0) {
            currentWin++;
            if (currentLoss > 0) {
                lossStreaks.push(currentLoss);
                currentLoss = 0;
            }
        } else if (t.netPnl < 0) {
            currentLoss++;
            if (currentWin > 0) {
                winStreaks.push(currentWin);
                currentWin = 0;
            }
        }
    }
    if (currentWin > 0) winStreaks.push(currentWin);
    if (currentLoss > 0) lossStreaks.push(currentLoss);

    return {
        maxWins: winStreaks.length > 0 ? Math.max(...winStreaks) : 0,
        maxLosses: lossStreaks.length > 0 ? Math.max(...lossStreaks) : 0,
        avgWins: winStreaks.length > 0 ? winStreaks.reduce((a, b) => a + b, 0) / winStreaks.length : 0,
        avgLosses: lossStreaks.length > 0 ? lossStreaks.reduce((a, b) => a + b, 0) / lossStreaks.length : 0,
    };
}

export function buildPnlHistogram(values: number[]): HistogramBin[] {
    if (values.length === 0) return [];
    const sorted = [...values].sort((a, b) => a - b);
    const min = sorted[0]!;
    const max = sorted[sorted.length - 1]!;
    const range = max - min;
    if (range === 0) return [{ range: fmtPnlShort(min), from: min, count: values.length, fill: NEUTRAL_COLOR }];

    const binCount = Math.min(30, Math.max(10, Math.ceil(Math.sqrt(values.length))));
    const binWidth = range / binCount;
    const bins: HistogramBin[] = [];

    for (let i = 0; i < binCount; i++) {
        const from = min + i * binWidth;
        const to = from + binWidth;
        const count = values.filter((v) => (i === binCount - 1 ? v >= from && v <= to : v >= from && v < to)).length;
        bins.push({
            range: `${fmtPnlShort(from)} – ${fmtPnlShort(to)}`,
            from,
            count,
            fill: from >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR,
        });
    }
    return bins;
}

/**
 * Closed trades bucketed by exit year and month, in exchange time. Each year's drawdown is walked
 * from zero in exit order, so it reads the same way as the overall max drawdown does.
 */
function buildMonthGrid(closedInExitOrder: Trade[]): YearMonthRow[] {
    const byYear = new Map<number, { months: (number | null)[]; trades: Trade[] }>();
    for (const t of closedInExitOrder) {
        const key = tradeDateKey(isValidDate(t.exitDate) ? t.exitDate : t.entryDate, "");
        if (key === "") continue;
        const year = Number(key.slice(0, 4));
        const month = Number(key.slice(5, 7)) - 1;
        const row = byYear.get(year) ?? { months: Array.from({ length: 12 }, () => null), trades: [] };
        row.months[month] = (row.months[month] ?? 0) + t.netPnl;
        row.trades.push(t);
        byYear.set(year, row);
    }
    return [...byYear.entries()]
        .sort(([a], [b]) => a - b)
        .map(([year, row]) => {
            let cumulative = 0;
            let peak = 0;
            let maxDrawdown = 0;
            for (const t of row.trades) {
                cumulative += t.netPnl;
                peak = Math.max(peak, cumulative);
                maxDrawdown = Math.max(maxDrawdown, peak - cumulative);
            }
            const pnl = row.trades.reduce((sum, t) => sum + t.netPnl, 0);
            return { year, months: row.months, pnl, maxDrawdown };
        });
}

/** Every P/L metric runs on closed trades only; open ones have no realized result yet. */
export function computeAnalysis(trades: Trade[]): Analysis {
    const closed = trades
        .filter((t) => !t.isOpen)
        .sort((a, b) => (isValidDate(b.exitDate) ? b.exitDate.getTime() : 0) - (isValidDate(a.exitDate) ? a.exitDate.getTime() : 0));

    const totalTrades = trades.length;
    const winningTrades = closed.filter((t) => t.netPnl > 0).length;
    const losingTrades = closed.filter((t) => t.netPnl < 0).length;
    const breakevenTrades = closed.length - winningTrades - losingTrades;

    const totalPnl = closed.reduce((s, t) => s + t.pnl, 0);
    const grossProfit = closed.filter((t) => t.netPnl > 0).reduce((s, t) => s + t.netPnl, 0);
    const grossLoss = Math.abs(closed.filter((t) => t.netPnl < 0).reduce((s, t) => s + t.netPnl, 0));
    const netPnl = closed.reduce((s, t) => s + t.netPnl, 0);
    const totalFees = closed.reduce((s, t) => s + t.fees, 0);
    const totalBrokerage = closed.reduce((s, t) => s + t.brokerage, 0);
    const totalSlippage = closed.reduce((s, t) => s + t.slippage, 0);
    const totalCosts = totalFees + totalBrokerage + totalSlippage;

    const winRate = closed.length > 0 ? (winningTrades / closed.length) * 100 : 0;
    const lossRate = closed.length > 0 ? (losingTrades / closed.length) * 100 : 0;
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;
    const expectancy = closed.length > 0 ? netPnl / closed.length : 0;

    const wins = closed.filter((t) => t.netPnl > 0).map((t) => t.netPnl);
    const losses = closed.filter((t) => t.netPnl < 0).map((t) => Math.abs(t.netPnl));
    const avgWin = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
    const payoffRatio = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? Infinity : 0;

    // Equity curve and drawdown, walked in exit order since that is when P/L is realized.
    let cumulative = 0;
    let peak = 0;
    /** When the running equity peak was set, which is where an underwater stretch begins. */
    let peakAt = 0;
    let underWater = false;
    let maxDrawdown = 0;
    let maxDrawdownPct = 0;
    let maxDrawdownDurationMs = 0;

    const equityCurve: EquityPoint[] = [{ date: "Start", equity: 0, drawdown: 0 }];
    const dailyPnlMap = new Map<string, number>();

    for (let i = closed.length - 1; i >= 0; i--) {
        const t = closed[i]!;
        cumulative += t.netPnl;
        const dateKey = tradeDateKey(isValidDate(t.exitDate) ? t.exitDate : t.entryDate);
        dailyPnlMap.set(dateKey, (dailyPnlMap.get(dateKey) ?? 0) + t.netPnl);

        const exitedAt = isValidDate(t.exitDate) ? t.exitDate.getTime() : 0;
        if (cumulative > peak) {
            // Back at a new high: the stretch that began at the old peak is over.
            if (underWater && peakAt > 0 && exitedAt > 0) {
                maxDrawdownDurationMs = Math.max(maxDrawdownDurationMs, exitedAt - peakAt);
            }
            peak = cumulative;
            peakAt = exitedAt;
            underWater = false;
        }
        const drawdown = peak - cumulative;
        // A percentage drawdown off a peak of zero or less is meaningless.
        if (peak > 0) maxDrawdownPct = Math.max(maxDrawdownPct, (drawdown / peak) * 100);
        if (drawdown > maxDrawdown) maxDrawdown = drawdown;
        if (drawdown > 0) underWater = true;

        equityCurve.push({ date: dateKey, equity: cumulative, drawdown: -drawdown });
    }
    // A drawdown still open at the end of the log runs from its peak to the last exit.
    const latestExit = closed[0] && isValidDate(closed[0].exitDate) ? closed[0].exitDate.getTime() : 0;
    if (underWater && peakAt > 0 && latestExit > peakAt) {
        maxDrawdownDurationMs = Math.max(maxDrawdownDurationMs, latestExit - peakAt);
    }

    const recoveryFactor = maxDrawdown > 0 ? netPnl / maxDrawdown : netPnl > 0 ? Infinity : 0;

    // Per-trade returns, annualised as a daily series would be.
    const tradeReturns = closed.map((t) => t.pnlPct);
    const avgReturn = tradeReturns.length > 0 ? tradeReturns.reduce((a, b) => a + b, 0) / tradeReturns.length : 0;
    const returnVariance =
        tradeReturns.length > 1
            ? tradeReturns.reduce((s, r) => s + (r - avgReturn) ** 2, 0) / (tradeReturns.length - 1)
            : 0;
    const returnStd = Math.sqrt(returnVariance);
    const sharpeRatio = returnStd > 0 ? (avgReturn / returnStd) * Math.sqrt(252) : 0;

    const downsideReturns = tradeReturns.filter((r) => r < 0);
    const downsideVariance =
        downsideReturns.length > 1
            ? downsideReturns.reduce((s, r) => s + r ** 2, 0) / downsideReturns.length
            : 0;
    const downsideStd = Math.sqrt(downsideVariance);
    const sortinoRatio = downsideStd > 0 ? (avgReturn / downsideStd) * Math.sqrt(252) : 0;

    const durations = closed
        .filter((t) => isValidDate(t.entryDate) && isValidDate(t.exitDate))
        .map((t) => Math.abs(t.exitDate.getTime() - t.entryDate.getTime()));
    const avgTradeDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
    const medianTradeDuration = median(durations);
    const longestTrade = durations.length > 0 ? Math.max(...durations) : 0;
    const shortestTrade = durations.length > 0 ? Math.min(...durations) : 0;

    const streaks = computeConsecutiveStreaks([...closed].reverse());

    const dailyPnl = [...dailyPnlMap.entries()]
        .map(([date, pnl]) => ({ date, pnl }))
        .sort((a, b) => a.date.localeCompare(b.date));

    const uniqueDays = dailyPnl.length;
    const bestDay = dailyPnl.length > 0 ? dailyPnl.reduce((a, b) => (a.pnl > b.pnl ? a : b)) : { date: "-", pnl: 0 };
    const worstDay = dailyPnl.length > 0 ? dailyPnl.reduce((a, b) => (a.pnl < b.pnl ? a : b)) : { date: "-", pnl: 0 };
    const avgDailyPnl = uniqueDays > 0 ? netPnl / uniqueDays : 0;

    const groupByPeriod = (period: "month" | "year") => {
        const map = new Map<string, { pnl: number; trades: number; wins: number }>();
        for (const t of closed) {
            const date = isValidDate(t.exitDate) ? t.exitDate : t.entryDate;
            // Bucketed in exchange time so a trade is filed under the session it settled in.
            const key = period === "month" ? tradeDateKey(date).slice(0, 7) : tradeDateKey(date).slice(0, 4);
            if (!key) continue;
            const existing = map.get(key) ?? { pnl: 0, trades: 0, wins: 0 };
            existing.pnl += t.netPnl;
            existing.trades++;
            if (t.netPnl > 0) existing.wins++;
            map.set(key, existing);
        }
        return map;
    };

    const monthlyMap = groupByPeriod("month");
    const monthlyPnl: MonthlyPnL[] = [...monthlyMap.entries()]
        .map(([key, v]) => {
            const [year, month] = key.split("-").map(Number) as [number, number];
            return {
                year: year ?? 0,
                month: month ?? 0,
                monthLabel: `${MONTHS[(month ?? 1) - 1]} ${year}`,
                pnl: v.pnl,
                trades: v.trades,
                winRate: v.trades > 0 ? (v.wins / v.trades) * 100 : 0,
            };
        })
        .sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));

    const bestMonth = monthlyPnl.length
        ? monthlyPnl.reduce((a, b) => (a.pnl > b.pnl ? a : b))
        : { year: 0, month: 0, monthLabel: "-", pnl: 0, trades: 0, winRate: 0 };
    const worstMonth = monthlyPnl.length
        ? monthlyPnl.reduce((a, b) => (a.pnl < b.pnl ? a : b))
        : { year: 0, month: 0, monthLabel: "-", pnl: 0, trades: 0, winRate: 0 };

    const entryWindowPnl = windowBreakdown(closed, (t) => t.entryDate);
    const exitWindowPnl = windowBreakdown(closed, (t) => (isValidDate(t.exitDate) ? t.exitDate : t.entryDate));

    const weekdayTotals = WEEKDAYS.map(() => ({ pnl: 0, trades: 0, wins: 0 }));
    for (const t of closed) {
        const key = tradeDateKey(isValidDate(t.exitDate) ? t.exitDate : t.entryDate, "");
        if (key === "") continue;
        const weekday = weekdayTotals[new Date(`${key}T00:00:00Z`).getUTCDay()]!;
        weekday.pnl += t.netPnl;
        weekday.trades++;
        if (t.netPnl > 0) weekday.wins++;
    }
    const weekdayPnl: WeekdayPnL[] = WEEKDAYS.map((day, i) => {
        const v = weekdayTotals[i]!;
        return { day, pnl: v.pnl, trades: v.trades, winRate: v.trades > 0 ? (v.wins / v.trades) * 100 : 0 };
    });

    const yearlyMap = groupByPeriod("year");
    const yearlyPnl: YearlyPnL[] = [...yearlyMap.entries()]
        .map(([year, v]) => ({
            year: Number(year),
            pnl: v.pnl,
            trades: v.trades,
            winRate: v.trades > 0 ? (v.wins / v.trades) * 100 : 0,
            avgPnl: v.trades > 0 ? v.pnl / v.trades : 0,
        }))
        .sort((a, b) => a.year - b.year);

    const bestYear = yearlyPnl.length > 0 ? yearlyPnl.reduce((a, b) => (a.pnl > b.pnl ? a : b)) : { year: 0, pnl: 0 };
    const worstYear = yearlyPnl.length > 0 ? yearlyPnl.reduce((a, b) => (a.pnl < b.pnl ? a : b)) : { year: 0, pnl: 0 };
    const avgMonthlyPnl = monthlyPnl.length > 0 ? netPnl / monthlyPnl.length : 0;

    const winningDays = dailyPnl.filter((d) => d.pnl > 0).map((d) => d.pnl);
    const losingDays = dailyPnl.filter((d) => d.pnl < 0).map((d) => -d.pnl);
    const dayCount = dailyPnl.length;
    const dayWinShare = dayCount > 0 ? winningDays.length / dayCount : 0;
    const dayLossShare = dayCount > 0 ? losingDays.length / dayCount : 0;
    const avgWinningDay = mean(winningDays);
    const avgLosingDay = mean(losingDays);
    const dailyExpectancyValue = dayWinShare * avgWinningDay - dayLossShare * avgLosingDay;
    const dailyExpectancy: DailyExpectancy = {
        days: dayCount,
        winningDays: winningDays.length,
        losingDays: losingDays.length,
        dayWinRate: dayWinShare * 100,
        avgWinningDay,
        avgLosingDay,
        expectancy: dailyExpectancyValue,
        expectancyRatio:
            avgLosingDay > 0 ? dailyExpectancyValue / avgLosingDay : winningDays.length > 0 ? Infinity : 0,
    };

    const expectancyRatio =
        avgLoss > 0 ? (winRate / 100) * payoffRatio - lossRate / 100 : wins.length > 0 ? Infinity : 0;

    const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
    let spanStart = Infinity;
    let spanEnd = -Infinity;
    for (const t of closed) {
        if (isValidDate(t.entryDate)) spanStart = Math.min(spanStart, t.entryDate.getTime());
        const end = isValidDate(t.exitDate) ? t.exitDate : t.entryDate;
        if (isValidDate(end)) spanEnd = Math.max(spanEnd, end.getTime());
    }
    const tradingYears = spanEnd > spanStart ? (spanEnd - spanStart) / MS_PER_YEAR : 0;
    const avgReturnToMaxDrawdown = tradingYears > 0 ? recoveryFactor / tradingYears : 0;
    const monthGrid = buildMonthGrid([...closed].reverse());

    const symbolMap = new Map<string, { pnl: number; trades: number; wins: number; best: number; worst: number }>();
    for (const t of closed) {
        const existing = symbolMap.get(t.symbol) ?? { pnl: 0, trades: 0, wins: 0, best: -Infinity, worst: Infinity };
        existing.pnl += t.netPnl;
        existing.trades++;
        if (t.netPnl > 0) existing.wins++;
        if (t.netPnl > existing.best) existing.best = t.netPnl;
        if (t.netPnl < existing.worst) existing.worst = t.netPnl;
        symbolMap.set(t.symbol, existing);
    }

    const symbolStats: SymbolStats[] = [...symbolMap.entries()]
        .map(([symbol, v]) => ({
            symbol,
            trades: v.trades,
            pnl: v.pnl,
            winRate: v.trades > 0 ? (v.wins / v.trades) * 100 : 0,
            avgPnl: v.trades > 0 ? v.pnl / v.trades : 0,
            bestTrade: v.best === -Infinity ? 0 : v.best,
            worstTrade: v.worst === Infinity ? 0 : v.worst,
        }))
        .sort((a, b) => b.pnl - a.pnl);

    const longTrades = closed.filter((t) => t.side === "LONG");
    const shortTrades = closed.filter((t) => t.side === "SHORT");
    const longPnl = longTrades.reduce((s, t) => s + t.netPnl, 0);
    const shortPnl = shortTrades.reduce((s, t) => s + t.netPnl, 0);
    const longWins = longTrades.filter((t) => t.netPnl > 0).length;
    const shortWins = shortTrades.filter((t) => t.netPnl > 0).length;

    const allNetPnl = closed.map((t) => t.netPnl);

    return {
        trades,
        closedTrades: closed.length,
        openTrades: trades.length - closed.length,
        totalTrades,
        winningTrades,
        losingTrades,
        breakevenTrades,
        totalPnl,
        grossProfit,
        grossLoss,
        netPnl,
        totalFees,
        totalBrokerage,
        totalSlippage,
        totalCosts,
        winRate,
        lossRate,
        profitFactor,
        expectancy,
        payoffRatio,
        avgWin,
        avgLoss,
        largestWin: allNetPnl.length > 0 ? Math.max(...allNetPnl) : 0,
        largestLoss: allNetPnl.length > 0 ? Math.min(...allNetPnl) : 0,
        medianWin: median(wins),
        medianLoss: median(losses.map((l) => -l)),
        maxDrawdown,
        maxDrawdownPct,
        maxDrawdownDurationMs,
        recoveryFactor,
        sharpeRatio,
        sortinoRatio,
        avgTradeDuration,
        medianTradeDuration,
        longestTrade,
        shortestTrade,
        maxConsecutiveWins: streaks.maxWins,
        maxConsecutiveLosses: streaks.maxLosses,
        avgConsecutiveWins: streaks.avgWins,
        avgConsecutiveLosses: streaks.avgLosses,
        bestDay,
        worstDay,
        bestMonth,
        worstMonth,
        bestYear,
        worstYear,
        avgDailyPnl,
        avgMonthlyPnl,
        expectancyRatio,
        tradingYears,
        avgReturnToMaxDrawdown,
        monthGrid,
        dailyExpectancy,
        longTrades: {
            count: longTrades.length,
            pnl: longPnl,
            winRate: longTrades.length > 0 ? (longWins / longTrades.length) * 100 : 0,
            avgPnl: longTrades.length > 0 ? longPnl / longTrades.length : 0,
        },
        shortTrades: {
            count: shortTrades.length,
            pnl: shortPnl,
            winRate: shortTrades.length > 0 ? (shortWins / shortTrades.length) * 100 : 0,
            avgPnl: shortTrades.length > 0 ? shortPnl / shortTrades.length : 0,
        },
        equityCurve,
        monthlyPnl,
        entryWindowPnl,
        exitWindowPnl,
        weekdayPnl,
        yearlyPnl,
        symbolStats,
        pnlDistribution: buildPnlHistogram(allNetPnl),
        dailyPnl,
    };
}

// ─── Filtering ──────────────────────────────────────────────────────────────

/** What the trades page narrows the log by. Numbers and dates are raw input text; blank means unbounded. */
export type TradeFilters = {
    /** Case-insensitive part of the symbol. */
    symbol: string;
    side: "all" | "long" | "short";
    status: "all" | "closed" | "open";
    /** Net P&L bounds, in currency. */
    pnlMin: string;
    pnlMax: string;
    /** Return bounds, in percent of entry value. */
    pnlPctMin: string;
    pnlPctMax: string;
    /** Entry date as exchange-time `yyyy-mm-dd`, inclusive on both ends. */
    entryFrom: string;
    entryTo: string;
};

export const EMPTY_TRADE_FILTERS: TradeFilters = {
    symbol: "",
    side: "all",
    status: "all",
    pnlMin: "",
    pnlMax: "",
    pnlPctMin: "",
    pnlPctMax: "",
    entryFrom: "",
    entryTo: "",
};

export function hasActiveTradeFilters(filters: TradeFilters): boolean {
    return (Object.keys(EMPTY_TRADE_FILTERS) as (keyof TradeFilters)[]).some(
        (key) => filters[key] !== EMPTY_TRADE_FILTERS[key],
    );
}

/** A numeric bound as typed; blank or malformed reads as no bound. */
function parseBound(raw: string): number | null {
    if (raw.trim() === "") return null;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : null;
}

function inBounds(value: number, min: number | null, max: number | null): boolean {
    return (min === null || value >= min) && (max === null || value <= max);
}

function inDateBounds(date: Date, from: string, to: string): boolean {
    if (from === "" && to === "") return true;
    // A trade with no timestamp on this leg cannot fall inside a date range.
    const key = tradeDateKey(date, "");
    if (key === "") return false;
    return (from === "" || key >= from) && (to === "" || key <= to);
}

/**
 * The trades that satisfy every active filter, in their original order. P&L and return bounds
 * only ever match closed trades, since an open one has no realized result to compare.
 */
export function filterTrades(trades: Trade[], filters: TradeFilters): Trade[] {
    const symbol = filters.symbol.trim().toLowerCase();
    const pnlMin = parseBound(filters.pnlMin);
    const pnlMax = parseBound(filters.pnlMax);
    const pctMin = parseBound(filters.pnlPctMin);
    const pctMax = parseBound(filters.pnlPctMax);
    const usesPnl = pnlMin !== null || pnlMax !== null || pctMin !== null || pctMax !== null;

    return trades.filter((t) => {
        if (symbol !== "" && !t.symbol.toLowerCase().includes(symbol)) return false;
        if (filters.side !== "all" && t.side !== filters.side.toUpperCase()) return false;
        if (filters.status === "open" && !t.isOpen) return false;
        if (filters.status === "closed" && t.isOpen) return false;
        if (usesPnl && t.isOpen) return false;
        if (!inBounds(t.netPnl, pnlMin, pnlMax)) return false;
        if (!inBounds(t.pnlPct, pctMin, pctMax)) return false;
        if (!inDateBounds(t.entryDate, filters.entryFrom, filters.entryTo)) return false;
        return true;
    });
}

export type TradeSortKey = "pnl" | "pnlPct" | "entryTime" | "exitTime";

export type TradeSort = { key: TradeSortKey; dir: "asc" | "desc" };

/** The sort value for a trade, or null when it has none (an open trade has no P&L or exit). */
function sortValue(trade: Trade, key: TradeSortKey): number | null {
    switch (key) {
        case "pnl":
            return trade.isOpen ? null : trade.netPnl;
        case "pnlPct":
            return trade.isOpen ? null : trade.pnlPct;
        case "entryTime":
            return isValidDate(trade.entryDate) ? trade.entryDate.getTime() : null;
        case "exitTime":
            return trade.isOpen || !isValidDate(trade.exitDate) ? null : trade.exitDate.getTime();
    }
}

/** Trades in the requested order. Trades with no value for the key always sink to the bottom. */
export function sortTrades(trades: Trade[], sort: TradeSort): Trade[] {
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...trades].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        if (va === null || vb === null) return va === null && vb === null ? 0 : va === null ? 1 : -1;
        return (va - vb) * direction;
    });
}

// ─── Formatting ─────────────────────────────────────────────────────────────

export function fmtPnl(value: number): string {
    return value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
}

export function fmtPnlShort(value: number): string {
    if (Math.abs(value) >= 1_00_000) return `${(value / 1_00_000).toFixed(1)}L`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return value.toFixed(0);
}

export function fmtPct(value: number, digits = 1): string {
    return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

export function fmtNum(value: number, digits = 1): string {
    return value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) return "-";
    const totalMins = Math.round(ms / 60000);
    const days = Math.floor(totalMins / (60 * 24));
    const hours = Math.floor((totalMins % (60 * 24)) / 60);
    const mins = totalMins % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
}

/** Renders a `yyyy-mm-dd` bucket key or a log timestamp in exchange time. */
export function fmtDate(value: string | Date): string {
    if (!value) return "-";
    const date = typeof value === "string" ? (value === "Start" ? null : parseIstDate(value)) : value;
    if (!date || !isValidDate(date)) return typeof value === "string" && value !== "" ? value : "-";
    return date.toLocaleDateString("en-IN", {
        timeZone: TRADE_LOG_TIMEZONE,
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export function fmtDateTime(date: Date): string {
    if (!isValidDate(date)) return "-";
    return `${date.toLocaleDateString("en-IN", {
        timeZone: TRADE_LOG_TIMEZONE,
        day: "2-digit",
        month: "short",
        year: "numeric",
    })}, ${date.toLocaleTimeString("en-IN", {
        timeZone: TRADE_LOG_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    })}`;
}

/** Short label for chart axes, from a `yyyy-mm-dd` bucket key. */
export function fmtDayTick(value: string): string {
    if (!value || value === "Start") return "";
    const date = parseIstDate(value);
    if (!isValidDate(date)) return "";
    return date.toLocaleDateString("en-IN", { timeZone: TRADE_LOG_TIMEZONE, month: "short", day: "numeric" });
}